import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { builderAssets, builderPrices } from '../../data/builder';
import { money } from '../QuantityStepper';
interface HardCaseDownsellProps {
  open: boolean;
  total: number;
  onAccept: () => void;
  onDecline: () => void;
  onClose: () => void;
}
const EASE = [0.23, 1, 0.32, 1] as const;

/** One contextual offer for a single X1 build without a case. Never added automatically. */
export function HardCaseDownsell({
  open,
  total,
  onAccept,
  onDecline,
  onClose
}: HardCaseDownsellProps) {
  const acceptRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    acceptRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  return <AnimatePresence data-id="element-372" data-mp="isc4frhm45cq">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: EASE
    }} className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 sm:items-center sm:p-6" onClick={onClose} data-id="element-373" data-mp="16wtrc0lohiiq">
          <motion.div role="dialog" aria-modal="true" aria-labelledby="case-downsell-title" initial={{
        opacity: 0,
        y: 24
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 24
      }} transition={{
        duration: 0.24,
        ease: EASE
      }} onClick={event => event.stopPropagation()} className="relative w-full max-w-md border-t-2 border-grit-500 bg-ink-900 p-5 sm:border-2 sm:p-6" data-id="element-374" data-mp="sxkaqztk8me8">
            <button type="button" onClick={onClose} aria-label="Close" className="absolute right-3 top-3 p-1.5 text-steel-400 transition-colors duration-150 ease-grit hover:text-offwhite focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-375" data-mp="29b10xhcwstyj">
              <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-376" data-mp="2sgyslgwrjql8" />
            </button>
            <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-grit-500" data-id="element-377" data-mp="2emy1988555uw">Before you go</p>
            <h2 id="case-downsell-title" className="mt-2 font-display text-3xl font-extrabold uppercase leading-none text-offwhite" data-id="element-378" data-mp="2in8actr8ccdz">
              Protect your X1.
            </h2>
            <div className="mt-4 flex items-center gap-4" data-id="element-379" data-mp="29p5zznllw7e1">
              <img src={builderAssets.hardCase} alt="Hard protective case" className="h-24 w-24 shrink-0 object-cover" data-id="element-380" data-mp="1vovbgr7rr4w1" />
              <p className="text-[15px] leading-relaxed text-steel-300" data-id="element-381" data-mp="1zr8db4jeainj">
                Add the hard protective case today for{' '}
                <span className="font-bold text-offwhite" data-id="element-382" data-mp="3q3e9aghd8k7q">{money(builderPrices.hardCaseDownsell)}</span> instead of{' '}
                <span className="line-through" data-id="element-383" data-mp="3u1vj57s688su">{money(builderPrices.hardCase)}</span>.
              </p>
            </div>
            <dl className="mt-4 grid grid-cols-[1fr_auto] gap-y-1 border-t border-white/10 pt-3 text-[14px]" data-id="element-384" data-mp="1efj8owo8bq84">
              <dt className="text-steel-400" data-id="element-385" data-mp="1s77fqljmfoi8">Current total</dt>
              <dd className="text-right tabular-nums text-steel-300" data-id="element-386" data-mp="1s8insxk3nif5">{money(total)}</dd>
              <dt className="font-semibold text-offwhite" data-id="element-387" data-mp="1nduumle5hzu8">With the case</dt>
              <dd className="text-right font-bold tabular-nums text-offwhite" data-id="element-388" data-mp="223j10116voot">{money(total + builderPrices.hardCaseDownsell)}</dd>
            </dl>
            <button ref={acceptRef} type="button" onClick={onAccept} className="mt-5 flex w-full items-center justify-center bg-grit-500 px-5 py-4 font-display text-xl font-extrabold uppercase tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite" data-id="element-389" data-mp="2b5jr8dy09agz">
              Add the case — {money(builderPrices.hardCaseDownsell)}
            </button>
            <button type="button" onClick={onDecline} className="mt-3 w-full py-2 text-center text-[13px] font-semibold text-steel-300 underline-offset-4 hover:text-offwhite hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-390" data-mp="i73dnknpz52r">
              No thanks, continue without a case
            </button>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}